/**
 * CampaignTemplatesPage — template gallery for `campaign-templates`.
 *
 * Renders a responsive card grid of available templates (tenant + platform
 * defaults merged by the API). Each card shows name, description preview,
 * vertical, scope badge, and a "Use this template" action that triggers the
 * instantiate endpoint. A create form below the gallery adds new tenant
 * overrides. Edit (inline rename) and delete actions live on each card.
 */
import { useMemo, useState, type FormEvent } from "react";
import type {
	CampaignTemplateDraft,
	CampaignTemplateRow,
} from "./api";
import {
	useCampaignTemplatesCreate,
	useCampaignTemplatesDelete,
	useCampaignTemplatesInstantiate,
	useCampaignTemplatesList,
	useCampaignTemplatesUpdate,
} from "./hooks";

const VERTICALS = [
	"igaming",
	"sports",
	"casino",
	"poker",
	"lottery",
	"esports",
	"other",
] as const;

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_USE =
	"rounded-[6px] bg-gradient-to-r from-emerald-600 to-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:from-emerald-700 hover:to-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

function previewText(payload: unknown): string {
	if (!payload) return "";
	try {
		const asString =
			typeof payload === "string" ? payload : JSON.stringify(payload);
		return asString.length > 140
			? `${asString.slice(0, 137)}...`
			: asString;
	} catch {
		return "";
	}
}

function ScopeBadge({ scope }: { scope: "platform" | "tenant" }) {
	const cls =
		scope === "platform"
			? "border-sky-200 bg-sky-50 text-sky-700"
			: "border-emerald-200 bg-emerald-50 text-emerald-700";
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
			data-testid={`campaign-templates-scope-${scope}`}
		>
			{scope}
		</span>
	);
}

function TemplateCard({
	row,
	onUse,
	onDelete,
	onRename,
	useLoading,
	deleteLoading,
	renameLoading,
}: {
	row: CampaignTemplateRow;
	onUse: (code: string) => void;
	onDelete: (id: string) => void;
	onRename: (id: string, nextName: string) => void;
	useLoading: boolean;
	deleteLoading: boolean;
	renameLoading: boolean;
}) {
	const [editing, setEditing] = useState(false);
	const [draftName, setDraftName] = useState(row.name);

	const canMutate = row.scope === "tenant";

	const submitRename = () => {
		const next = draftName.trim();
		if (!next || next === row.name) {
			setEditing(false);
			setDraftName(row.name);
			return;
		}
		onRename(row.id, next);
		setEditing(false);
	};

	return (
		<article
			className={`${CARD} flex flex-col gap-3`}
			data-testid={`campaign-templates-card-${row.id}`}
		>
			<header className="flex items-start justify-between gap-3">
				<div className="flex flex-1 flex-col gap-1">
					{editing ? (
						<input
							className={INPUT}
							value={draftName}
							autoFocus
							onChange={(e) => setDraftName(e.currentTarget.value)}
							onBlur={submitRename}
							onKeyDown={(e) => {
								if (e.key === "Enter") submitRename();
								if (e.key === "Escape") {
									setEditing(false);
									setDraftName(row.name);
								}
							}}
							disabled={renameLoading}
							data-testid={`campaign-templates-rename-input-${row.id}`}
						/>
					) : (
						<h3
							className="text-base font-semibold text-zinc-900"
							data-testid={`campaign-templates-name-${row.id}`}
						>
							{row.name}
						</h3>
					)}
					<p className="font-mono text-[11px] text-zinc-500">
						{row.template_code}
					</p>
				</div>
				<ScopeBadge scope={row.scope} />
			</header>

			{row.description ? (
				<p className="text-sm text-zinc-600">{row.description}</p>
			) : null}

			{row.vertical ? (
				<p className="text-xs text-zinc-500">
					Vertical:{" "}
					<span className="font-medium text-zinc-700">{row.vertical}</span>
				</p>
			) : null}

			<pre
				className="max-h-28 overflow-auto rounded-[6px] border border-zinc-200 bg-zinc-50 p-2 font-mono text-[11px] text-zinc-700"
				data-testid={`campaign-templates-preview-${row.id}`}
			>
				{previewText(row.payload) || "{}"}
			</pre>

			<footer className="mt-auto flex flex-wrap items-center justify-between gap-2">
				<button
					type="button"
					className={BTN_USE}
					onClick={() => onUse(row.template_code)}
					disabled={useLoading}
					data-testid={`campaign-templates-use-${row.id}`}
				>
					{useLoading ? "Loading." : "Use this template"}
				</button>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN_GHOST}
						onClick={() => setEditing((v) => !v)}
						disabled={!canMutate || renameLoading}
						title={canMutate ? "Rename" : "Platform template. Clone to edit."}
						data-testid={`campaign-templates-edit-${row.id}`}
					>
						Edit
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={() => {
							if (!canMutate) return;
							onDelete(row.id);
						}}
						disabled={!canMutate || deleteLoading}
						title={canMutate ? "Delete" : "Platform template. Cannot delete."}
						data-testid={`campaign-templates-delete-${row.id}`}
					>
						Delete
					</button>
				</div>
			</footer>
		</article>
	);
}

export function CampaignTemplatesPage() {
	const [verticalFilter, setVerticalFilter] = useState<string>("");
	const listQuery = useCampaignTemplatesList(
		verticalFilter ? { vertical: verticalFilter } : undefined,
	);
	const createMut = useCampaignTemplatesCreate();
	const updateMut = useCampaignTemplatesUpdate();
	const deleteMut = useCampaignTemplatesDelete();
	const instantiateMut = useCampaignTemplatesInstantiate();

	const [draftCode, setDraftCode] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDesc, setDraftDesc] = useState("");
	const [draftVertical, setDraftVertical] = useState<string>("");
	const [draftPayload, setDraftPayload] = useState("{}");
	const [payloadError, setPayloadError] = useState<string | null>(null);
	const [lastDraft, setLastDraft] = useState<CampaignTemplateDraft | null>(null);
	const [pendingUseCode, setPendingUseCode] = useState<string | null>(null);

	const items = useMemo(() => listQuery.data ?? [], [listQuery.data]);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const code = draftCode.trim();
		const name = draftName.trim();
		if (!code || !name) return;

		let payload: unknown = {};
		const raw = draftPayload.trim();
		if (raw) {
			try {
				payload = JSON.parse(raw);
				setPayloadError(null);
			} catch (err) {
				setPayloadError(
					`Invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
				);
				return;
			}
		}

		createMut.mutate(
			{
				template_code: code,
				name,
				description: draftDesc.trim() ? draftDesc.trim() : null,
				vertical: draftVertical ? draftVertical : null,
				payload,
			},
			{
				onSuccess: () => {
					setDraftCode("");
					setDraftName("");
					setDraftDesc("");
					setDraftVertical("");
					setDraftPayload("{}");
					setPayloadError(null);
				},
			},
		);
	};

	const handleUse = (code: string) => {
		setPendingUseCode(code);
		instantiateMut.mutate(code, {
			onSuccess: (draft) => {
				setLastDraft(draft);
				setPendingUseCode(null);
			},
			onError: () => {
				setPendingUseCode(null);
			},
		});
	};

	const handleDelete = (id: string) => {
		deleteMut.mutate(id);
	};

	const handleRename = (id: string, nextName: string) => {
		updateMut.mutate({ id, patch: { name: nextName } });
	};

	return (
		<main
			data-testid="campaign-templates-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					campaign-templates
				</p>
				<h1
					className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent"
					data-testid="campaign-templates-title"
				>
					Campaign Template Gallery
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Pre-built campaign recipes. Use a platform default as-is, clone it to
					your tenant, or author a fresh tenant template below.
				</p>
			</header>

			<section
				className="flex flex-wrap items-center gap-3"
				aria-label="Filter campaign templates"
			>
				<label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Vertical
				</label>
				<select
					className={`${INPUT} w-auto min-w-[10rem]`}
					value={verticalFilter}
					onChange={(e) => setVerticalFilter(e.currentTarget.value)}
					data-testid="campaign-templates-vertical-filter"
				>
					<option value="">All verticals</option>
					{VERTICALS.map((v) => (
						<option key={v} value={v}>
							{v}
						</option>
					))}
				</select>
				<span
					className="ml-auto text-xs text-zinc-500"
					data-testid="campaign-templates-count"
				>
					{items.length} template{items.length === 1 ? "" : "s"}
				</span>
			</section>

			{listQuery.isLoading ? (
				<p
					className="text-sm text-zinc-500"
					data-testid="campaign-templates-loading"
				>
					Loading templates.
				</p>
			) : listQuery.error ? (
				<p className="text-sm text-red-600" role="alert">
					{listQuery.error.message}
				</p>
			) : items.length === 0 ? (
				<div
					className={`${CARD} text-sm text-zinc-500`}
					data-testid="campaign-templates-empty"
				>
					No templates yet. Create your first tenant template below.
				</div>
			) : (
				<section
					className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
					data-testid="campaign-templates-gallery"
					aria-label="Campaign template gallery"
				>
					{items.map((row) => (
						<TemplateCard
							key={row.id}
							row={row}
							onUse={handleUse}
							onDelete={handleDelete}
							onRename={handleRename}
							useLoading={
								instantiateMut.isPending &&
								pendingUseCode === row.template_code
							}
							deleteLoading={
								deleteMut.isPending && deleteMut.variables === row.id
							}
							renameLoading={
								updateMut.isPending &&
								(updateMut.variables?.id ?? "") === row.id
							}
						/>
					))}
				</section>
			)}

			{lastDraft ? (
				<section
					className={`${CARD} border-emerald-200 bg-emerald-50/50`}
					data-testid="campaign-templates-draft"
					aria-live="polite"
				>
					<div className="flex items-start justify-between gap-3">
						<div>
							<h2 className="text-base font-semibold text-zinc-900">
								Draft seeded
							</h2>
							<p className="mt-1 text-sm text-zinc-700">
								Template{" "}
								<span className="font-mono">{lastDraft.template_code}</span>{" "}
								ready. Wire it into your campaign wizard.
							</p>
						</div>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={() => setLastDraft(null)}
							data-testid="campaign-templates-draft-dismiss"
						>
							Dismiss
						</button>
					</div>
				</section>
			) : null}

			<section className={CARD} aria-label="Create new campaign template">
				<h2 className="text-base font-semibold text-zinc-900">New template</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Authors a tenant-scoped template. Platform defaults are managed by
					superadmins only.
				</p>
				<form
					onSubmit={handleCreate}
					className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="campaign-templates-create-form"
				>
					<div className="flex flex-col gap-1">
						<label className="text-xs font-medium text-zinc-700">
							Template code
						</label>
						<input
							type="text"
							className={INPUT}
							placeholder="welcome-bonus-v1"
							value={draftCode}
							onChange={(e) => setDraftCode(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="campaign-templates-code-input"
							required
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className="text-xs font-medium text-zinc-700">Name</label>
						<input
							type="text"
							className={INPUT}
							placeholder="Welcome Bonus"
							value={draftName}
							onChange={(e) => setDraftName(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="campaign-templates-name-input"
							required
						/>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label className="text-xs font-medium text-zinc-700">
							Description
						</label>
						<input
							type="text"
							className={INPUT}
							placeholder="Short summary of what this template does."
							value={draftDesc}
							onChange={(e) => setDraftDesc(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="campaign-templates-desc-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className="text-xs font-medium text-zinc-700">
							Vertical
						</label>
						<select
							className={INPUT}
							value={draftVertical}
							onChange={(e) => setDraftVertical(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="campaign-templates-vertical-input"
						>
							<option value="">None</option>
							{VERTICALS.map((v) => (
								<option key={v} value={v}>
									{v}
								</option>
							))}
						</select>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label className="text-xs font-medium text-zinc-700">
							Payload (JSON)
						</label>
						<textarea
							className={`${INPUT} min-h-[7rem] font-mono text-xs`}
							value={draftPayload}
							onChange={(e) => setDraftPayload(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="campaign-templates-payload-input"
						/>
						{payloadError ? (
							<p className="text-xs text-red-600" role="alert">
								{payloadError}
							</p>
						) : null}
					</div>
					<div className="sm:col-span-2 flex items-center justify-between gap-3">
						{createMut.error ? (
							<p className="text-xs text-red-600" role="alert">
								{createMut.error.message}
							</p>
						) : (
							<span />
						)}
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={
								createMut.isPending ||
								!draftCode.trim() ||
								!draftName.trim()
							}
							data-testid="campaign-templates-submit"
						>
							{createMut.isPending ? "Saving." : "Create template"}
						</button>
					</div>
				</form>
			</section>
		</main>
	);
}

export default CampaignTemplatesPage;
